import { PagePermission } from '@jiaofa/constants'

export interface PagePermissionItem {
  code: string
  name: string
}

export interface PagePermissionModule {
  code: string
  label: string
  children: PagePermissionItem[]
}

/**
 * 页面级权限目录（二级折叠树数据源）。
 *
 * 与 `data/operationDesign.ts` 的 `operationMenus`（菜单）对齐：模块分组 + 页面清单一致。
 * 每个页面拆「查看」「操作」两个子权限，对应 `PagePermission` 的 `view`/`edit`。
 *
 * 注意：这是「阶段 A 前端先行」的本地清单。后端 `sys_permission` 目录当前仍是动作级（view/edit），
 * 尚未迁移到页面级；后端对齐后，本清单应改为由 `GET /system/permissions` 下发。
 */
export const pagePermissionModules: PagePermissionModule[] = [
  {
    code: 'dashboard',
    label: '运营总览',
    children: [
      { code: PagePermission.Dashboard.view, name: '查看' },
      { code: PagePermission.Dashboard.edit, name: '操作' },
    ],
  },
  {
    code: 'order',
    label: '交易履约',
    children: [
      { code: PagePermission.OrderCenter.view, name: '订单中心-查看' },
      { code: PagePermission.OrderCenter.edit, name: '订单中心-操作' },
      { code: PagePermission.OrderRefunds.view, name: '退款处理-查看' },
      { code: PagePermission.OrderRefunds.edit, name: '退款处理-操作' },
      { code: PagePermission.OrderComplaints.view, name: '投诉建议-查看' },
      { code: PagePermission.OrderComplaints.edit, name: '投诉建议-操作' },
    ],
  },
  {
    code: 'store',
    label: '门店管理',
    children: [
      { code: PagePermission.StoreList.view, name: '门店列表-查看' },
      { code: PagePermission.StoreList.edit, name: '门店列表-操作' },
      { code: PagePermission.StorePrinters.view, name: '打印设备-查看' },
      { code: PagePermission.StorePrinters.edit, name: '打印设备-操作' },
    ],
  },
  {
    code: 'product',
    label: '商品中心',
    children: [
      { code: PagePermission.ProductCategories.view, name: '商品分类-查看' },
      { code: PagePermission.ProductCategories.edit, name: '商品分类-操作' },
      { code: PagePermission.ProductList.view, name: '商品列表-查看' },
      { code: PagePermission.ProductList.edit, name: '商品列表-操作' },
      { code: PagePermission.ProductCombos.view, name: '商品套餐-查看' },
      { code: PagePermission.ProductCombos.edit, name: '商品套餐-操作' },
      { code: PagePermission.ProductExtras.view, name: '加料管理-查看' },
      { code: PagePermission.ProductExtras.edit, name: '加料管理-操作' },
    ],
  },
  {
    code: 'delivery',
    label: '配送与计价',
    children: [
      { code: PagePermission.DeliveryMinAmount.view, name: '起送金额-查看' },
      { code: PagePermission.DeliveryMinAmount.edit, name: '起送金额-操作' },
      { code: PagePermission.DeliveryAreas.view, name: '门店配送范围-查看' },
      { code: PagePermission.DeliveryAreas.edit, name: '门店配送范围-操作' },
      { code: PagePermission.DeliveryPoints.view, name: '配送点管理-查看' },
      { code: PagePermission.DeliveryPoints.edit, name: '配送点管理-操作' },
    ],
  },
  {
    code: 'drone',
    label: '无人机管理',
    children: [
      { code: PagePermission.DroneDevices.view, name: '无人机设备-查看' },
      { code: PagePermission.DroneDevices.edit, name: '无人机设备-操作' },
      { code: PagePermission.DroneTasks.view, name: '任务调度-查看' },
      { code: PagePermission.DroneTasks.edit, name: '任务调度-操作' },
      { code: PagePermission.DroneFlights.view, name: '飞行记录-查看' },
      { code: PagePermission.DroneFlights.edit, name: '飞行记录-操作' },
      { code: PagePermission.DroneFaults.view, name: '故障管理-查看' },
      { code: PagePermission.DroneFaults.edit, name: '故障管理-操作' },
      { code: PagePermission.DroneMaintenances.view, name: '维护管理-查看' },
      { code: PagePermission.DroneMaintenances.edit, name: '维护管理-操作' },
    ],
  },
  {
    code: 'user',
    label: '用户管理',
    children: [
      { code: PagePermission.UserList.view, name: '查看' },
      { code: PagePermission.UserList.edit, name: '操作' },
    ],
  },
  {
    code: 'member',
    label: '会员中心',
    children: [
      { code: PagePermission.MemberLevels.view, name: '会员等级-查看' },
      { code: PagePermission.MemberLevels.edit, name: '会员等级-操作' },
      { code: PagePermission.MemberRules.view, name: '规则配置-查看' },
      { code: PagePermission.MemberRules.edit, name: '规则配置-操作' },
      { code: PagePermission.MemberBenefits.view, name: '会员权益-查看' },
      { code: PagePermission.MemberBenefits.edit, name: '会员权益-操作' },
    ],
  },
  {
    code: 'marketing',
    label: '营销中心',
    children: [
      { code: PagePermission.MarketingCoupons.view, name: '优惠券-查看' },
      { code: PagePermission.MarketingCoupons.edit, name: '优惠券-操作' },
      { code: PagePermission.MarketingPointsMall.view, name: '积分商城-查看' },
      { code: PagePermission.MarketingPointsMall.edit, name: '积分商城-操作' },
      { code: PagePermission.MarketingCards.view, name: '周卡月卡-查看' },
      { code: PagePermission.MarketingCards.edit, name: '周卡月卡-操作' },
      { code: PagePermission.MarketingRecharge.view, name: '充值套餐-查看' },
      { code: PagePermission.MarketingRecharge.edit, name: '充值套餐-操作' },
      { code: PagePermission.MarketingBanners.view, name: '首页 Banner-查看' },
      { code: PagePermission.MarketingBanners.edit, name: '首页 Banner-操作' },
      { code: PagePermission.MarketingGroupBuy.view, name: '拼团活动-查看' },
      { code: PagePermission.MarketingGroupBuy.edit, name: '拼团活动-操作' },
      { code: PagePermission.MarketingBargain.view, name: '砍价活动-查看' },
      { code: PagePermission.MarketingBargain.edit, name: '砍价活动-操作' },
      { code: PagePermission.MarketingInvite.view, name: '邀请有礼-查看' },
      { code: PagePermission.MarketingInvite.edit, name: '邀请有礼-操作' },
      { code: PagePermission.MarketingFlashSale.view, name: '限时秒杀-查看' },
      { code: PagePermission.MarketingFlashSale.edit, name: '限时秒杀-操作' },
    ],
  },
  {
    code: 'data',
    label: '数据中心',
    children: [
      { code: PagePermission.DataOverview.view, name: '经营概览-查看' },
      { code: PagePermission.DataOverview.edit, name: '经营概览-操作' },
      { code: PagePermission.DataOrders.view, name: '订单分析-查看' },
      { code: PagePermission.DataOrders.edit, name: '订单分析-操作' },
      { code: PagePermission.DataProducts.view, name: '商品分析-查看' },
      { code: PagePermission.DataProducts.edit, name: '商品分析-操作' },
      { code: PagePermission.DataStoreRank.view, name: '门店排行-查看' },
      { code: PagePermission.DataStoreRank.edit, name: '门店排行-操作' },
    ],
  },
  {
    code: 'finance',
    label: '财务与支付',
    children: [
      { code: PagePermission.FinanceTransactions.view, name: '交易流水-查看' },
      { code: PagePermission.FinanceTransactions.edit, name: '交易流水-操作' },
      { code: PagePermission.FinanceReconciliation.view, name: '门店对账-查看' },
      { code: PagePermission.FinanceReconciliation.edit, name: '门店对账-操作' },
      { code: PagePermission.FinanceRefunds.view, name: '退款流水-查看' },
      { code: PagePermission.FinanceRefunds.edit, name: '退款流水-操作' },
      { code: PagePermission.FinanceProfitSharing.view, name: '微信分账-查看' },
      { code: PagePermission.FinanceProfitSharing.edit, name: '微信分账-操作' },
      { code: PagePermission.FinanceProfitSharingRules.view, name: '分账规则-查看' },
      { code: PagePermission.FinanceProfitSharingRules.edit, name: '分账规则-操作' },
      { code: PagePermission.FinanceProfitSharingReceivers.view, name: '分账接收方-查看' },
      { code: PagePermission.FinanceProfitSharingReceivers.edit, name: '分账接收方-操作' },
    ],
  },
  {
    code: 'system',
    label: '系统管理',
    children: [
      { code: PagePermission.SystemAdmins.view, name: '管理员-查看' },
      { code: PagePermission.SystemAdmins.edit, name: '管理员-操作' },
      { code: PagePermission.SystemRoles.view, name: '角色权限-查看' },
      { code: PagePermission.SystemRoles.edit, name: '角色权限-操作' },
      { code: PagePermission.SystemLogs.view, name: '操作日志-查看' },
      { code: PagePermission.SystemLogs.edit, name: '操作日志-操作' },
      { code: PagePermission.SystemParams.view, name: '系统参数-查看' },
      { code: PagePermission.SystemParams.edit, name: '系统参数-操作' },
      { code: PagePermission.SystemMessages.view, name: '消息中心-查看' },
      { code: PagePermission.SystemMessages.edit, name: '消息中心-操作' },
    ],
  },
]
